<script lang="ts" setup>
import { computed, provide, ref, toRef, watch } from 'vue'
import { useScroll } from '@vueuse/core'
import { bookConfig as rocnovelConfig } from './configs/rocnovel'




















import { useBookPage } from './composables/useBookPage'
import BookLoadingDefault from './components/BookLoading.default.vue'


import BookPageDefault from './components/BookPage.default.vue'










import { brand } from '@/config/brands'
import { bannerShow, useBannerShow } from '@/composables/useBannerShow'
import { useBottomOverscroll } from '@/composables/useBottomOverscroll'
import { loadBrandComponent, loadBrandConfig } from '@/utils/loadBrandConfig'
import { useFbqViewContent } from '@/composables/fbq/useFbqViewContent'
import { t } from '@/i18n/utils'
import { useAudioPlayerStore } from '@/stores'

const props = defineProps<{
  contentId: string
}>()

// 🔥 加载当前品牌配置（构建时 tree-shaking）
const config = loadBrandConfig({ rocnovel: rocnovelConfig })

// 🔥 加载当前品牌组件（rocnovel 和 oceread 共用 default，ficmoon 用专属组件）
const BookPage = loadBrandComponent({ rocnovel: BookPageDefault })
const BookLoading = loadBrandComponent({ rocnovel: BookLoadingDefault })

// 🔥 使用 composable 管理页面逻辑
const {
  book,
  coverUrl,
  introduce,
  paragraphs,
  skeleton,
  popupTOC,
  currentContentId,
  isInShelf,
  shelfLoading,
  onRead,
  onPullUpToNext,
  onToggleShelf,
  openPopupTOC,
  gotoTags,
  gotoBack,
} = useBookPage(toRef(props, 'contentId'), config)

// 容器引用（用于滚动等）
const containerRef = ref<HTMLElement>()
provide('containerRef', containerRef)

// 初始化 banner 显示逻辑
useBannerShow()

const audioStore = useAudioPlayerStore()
const { reportViewContent } = useFbqViewContent()

const stop = watch(() => book.value, (val) => {
  if (val) {
    reportViewContent(val.id, val.contentName, 'book')
    stop()
  }
}, { immediate: true })

// 底部上滑跳章节：在已触底的前提下，继续上滑累计超过阈值才跳转（顺滑、可靠）
// 触底加 6px 容差，规避 rem/缩放导致的亚像素误差，避免「滑到底却判定未触底」
const { arrivedState } = useScroll(containerRef, { offset: { bottom: 6 } })

const previewActive = ref(true)
let jumping = false
function handlePullUp() {
  if (jumping)
    return
  jumping = true
  onPullUpToNext()
  // 跳转失败（如无章节）时自愈，允许再次尝试
  setTimeout(() => {
    jumping = false
  }, 1000)
}

const { startListen: startOverscrollListen, stopListen: stopOverscrollListen } = useBottomOverscroll({
  containerRef,
  isAtBottom: computed(() => arrivedState.bottom),
  enabled: computed(() => !!book.value?.firstChapter && (brand !== 'superstory' || previewActive.value)),
  // 需在触底后继续上滑累计较大距离才触发，避免轻微滑动误跳
  threshold: 150,
  onTrigger: handlePullUp,
})

// 音频弹窗
function onOpenAudio() {
  if (!book.value)
    return
  const chapterId = currentContentId.value || book.value?.firstChapter?.id
  if (!chapterId)
    return
  audioStore.openPlayer({
    contentId: props.contentId,
    contentName: book.value.contentName,
    contentCoverUrl: book.value.contentCoverUrl || '',
    chapterId,
  })
}

// 容器在 book 加载完成后才渲染，待其挂载/重建后再绑定监听
watch(containerRef, (el) => {
  stopOverscrollListen()
  if (el)
    startOverscrollListen()
})
</script>

<template>
  <div>
    <!-- 预加载图片 -->
    <div style="display: none;">
      <img :src="coverUrl" alt="Preload">
    </div>

    <!-- Loading 状态 -->
    <div v-if="skeleton">
      <component :is="BookLoading" :loading-text="config.texts.loading" />
    </div>

    <!-- 书籍不存在 -->
    <div v-else-if="!book">
      <BizBlockEmpty :title="config.texts.booksNotExist" />
    </div>

    <!-- 主内容 -->
    <div v-else ref="containerRef" class="h-100dvh pb-20 bg-n-bg-global overflow-scroll">
      <!-- 下载 Banner -->
      <BizDownloadApp
        v-if="bannerShow"
        :id="contentId"
        position="fixed"
        class="fixed top-0 w-full z-9999"
        from="book"
      />

      <!-- 🔥 品牌专属页面组件（封面 + 内容） -->
      <BookPage
        :popup-t-o-c="popupTOC"
        :book="book"
        :config="config"
        :cover-url="coverUrl"
        :introduce="introduce"
        :paragraphs="paragraphs"
        :banner-show="bannerShow"
        :is-in-shelf="isInShelf"
        :shelf-loading="shelfLoading"
        :current-content-id="currentContentId"
        @update:popup-t-o-c="popupTOC = $event"
        @goto-tags="gotoTags"
        @open-toc="openPopupTOC"
        @read="onRead"
        @back="gotoBack"
        @toggle-shelf="onToggleShelf"
        @audio="onOpenAudio"
        @continue-preview="onPullUpToNext"
        @pull-up="handlePullUp"
        @update:preview-active="previewActive = $event"
      />

      <!-- 底部上滑跳章节引导（点击也可触发，兜底下拉失败） -->
      <!-- swipeUpInline 的品牌把这块提示渲染在第一章预览卡内部，此处不再重复 -->
      <div
        v-if="brand !== 'superstory' && !config.theme.swipeUpInline"
        class="pt-10px pb-20px flex flex-col items-center text-13px text-n-primary cursor-pointer transition-opacity"
        :class="arrivedState.bottom ? 'opacity-100' : 'opacity-60'"
        @click="handlePullUp"
      >
        <i-icon-park-outline-up class="text-18px animate-bounce" />
        <span class="mt-4px">{{ t('SwipeUpToContinue') }}</span>
      </div>
    </div>
  </div>
</template>
